// Baselayer Background Checks — raw HTTP transport for the staff module.
// Hooks in @/hooks/backgroundChecks wrap these with TanStack Query.

import { api } from "../client";

// ---- wire types ------------------------------------------------------

export type BackgroundCheckType =
  | "Kyb"
  | "Ofac"
  | "Pep"
  | "Dmf"
  | "Online"
  | "LienSearch";

export type BackgroundCheckStatus =
  | "Draft"
  | "Submitted"
  | "Processing"
  | "Completed"
  | "Failed"
  | "Filed";

export interface BackgroundCheckDto {
  id: string;
  dealId: string | null;
  checkType: BackgroundCheckType;
  status: BackgroundCheckStatus;
  subjectKind: string;
  subjectName: string;
  baselayerRequestId: string | null;
  environment: string;
  createdAt: string;
  completedAt: string | null;
  filedAt: string | null;
  dmsFileId: string | null;
}

export interface BackgroundCheckResultDto {
  verified: boolean | null;
  nameMatch: boolean | null;
  addressMatch: boolean | null;
  tinMatched: boolean | null;
  overallScore: number | null;
  scoresJson: string | null;
  watchlistHitsJson: string | null;
  lienFilingsJson: string | null;
  rawResponseJson: string;
  receivedAt: string;
}

export interface BackgroundCheckDetailDto {
  check: BackgroundCheckDto;
  result: BackgroundCheckResultDto | null;
}

export interface RunBackgroundCheckRequest {
  type: BackgroundCheckType;
  subjectKind: string;
  subjectName: string;
  dealId?: string | null;
  loanId?: string | null;
  subjectBusinessId?: string | null;
  subjectPersonId?: string | null;
  subjectEntityId?: string | null;
  address?: string | null;
  tin?: string | null;
  ssn?: string | null;
  officerNames?: string[];
  baselayerBusinessId?: string | null;
  lienSearchStates?: string[];
  websiteAnalysis: boolean;
  naicsPrediction: boolean;
  enhanced: boolean;
}

export interface FilePdfRequest {
  folderId: string;
}

export interface BackgroundChecksListParams {
  dealId?: string;
  status?: string;
  type?: string;
  subject?: string;
}

// ---- transport -------------------------------------------------------

export const backgroundChecksApi = {
  list: (params?: BackgroundChecksListParams) =>
    api.get<BackgroundCheckDto[]>("/background-checks", {
      dealId: params?.dealId,
      status: params?.status,
      type: params?.type,
      subject: params?.subject,
    }),
  get: (id: string) =>
    api.get<BackgroundCheckDetailDto>(`/background-checks/${id}`),
  run: (body: RunBackgroundCheckRequest) =>
    api.post<BackgroundCheckDto>("/background-checks/run", body),
  /** Manual authoritative re-fetch of the check's state from Baselayer. */
  refresh: (id: string) =>
    api.post<BackgroundCheckDetailDto>(`/background-checks/${id}/refresh`),
  filePdf: (id: string, body: FilePdfRequest) =>
    api.post<BackgroundCheckDto>(`/background-checks/${id}/file-pdf`, body),
};
